/**
 * Generic auth form component
 * Handles all 12 auth forms with configuration
 */

import { BaseComponent } from '../base-component';
import type { ApiResponse, TabId } from '../../types';
import { AuthAPI } from '../../services/auth-api';
import { AuthStore } from '../../state/auth-store';
import { TurnstileManager } from '../../services/turnstile';

interface FormField {
  id: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'number' | 'url' | 'checkbox' | 'select' | 'textarea';
  placeholder?: string;
  required?: boolean;
  pattern?: string;
  options?: Array<{ value: string; label: string }>;
  small?: string;
  rows?: number;
  value?: string;
}

interface AuthFormConfig {
  id: TabId;
  title: string;
  description: string;
  badges: Array<{ text: string; class: string }>;
  requiresTurnstile: boolean;
  fields: FormField[];
  submitButton: string;
  secondaryButtons?: Array<{ text: string; action: string }>;
}

export class GenericAuthForm extends BaseComponent {
  private config: AuthFormConfig;
  private authAPI: AuthAPI;
  private authStore: AuthStore;
  private turnstileManager?: TurnstileManager;
  private responseElementId: string;
  private turnstileContainerId?: string;

  constructor(
    container: HTMLElement,
    config: AuthFormConfig,
    authAPI: AuthAPI,
    authStore: AuthStore,
    turnstileManager?: TurnstileManager
  ) {
    super(container);
    this.config = config;
    this.authAPI = authAPI;
    this.authStore = authStore;
    this.turnstileManager = turnstileManager;
    this.responseElementId = `${config.id}-response`;

    if (config.requiresTurnstile && turnstileManager) {
      this.turnstileContainerId = `turnstile-${config.id}`;
    }
  }

  render(): void {
    this.container.innerHTML = this.getFormHTML();
    this.setupFormListener();

    // Render Turnstile widget if needed
    if (this.turnstileContainerId && this.turnstileManager) {
      requestAnimationFrame(() => {
        this.turnstileManager!.renderWidget(this.turnstileContainerId!);
      });
    }

    // Setup secondary button listeners
    this.setupSecondaryButtons();
  }

  private getFormHTML(): string {
    const badges = this.config.badges
      .map((badge) => `<span class="badge ${badge.class}">${badge.text}</span>`)
      .join('');

    const fields = this.config.fields
      .map((field) => {
        if (field.type === 'checkbox') {
          return `
            <div class="form-group">
              <label>
                <input type="checkbox" id="${field.id}" ${field.required ? 'required' : ''}>
                ${field.label}
              </label>
              ${field.small ? `<small>${field.small}</small>` : ''}
            </div>
          `;
        }

        if (field.type === 'select') {
          const options = (field.options || [])
            .map((opt) => `<option value="${opt.value}">${opt.label}</option>`)
            .join('');
          return `
            <div class="form-group">
              <label>${field.label}</label>
              <select id="${field.id}" ${field.required ? 'required' : ''}>${options}</select>
              ${field.small ? `<small>${field.small}</small>` : ''}
            </div>
          `;
        }

        if (field.type === 'textarea') {
          return `
            <div class="form-group">
              <label>${field.label}</label>
              <textarea id="${field.id}" placeholder="${field.placeholder || ''}" ${field.required ? 'required' : ''} rows="${field.rows || 3}">${field.value || ''}</textarea>
              ${field.small ? `<small>${field.small}</small>` : ''}
            </div>
          `;
        }

        return `
          <div class="form-group">
            <label>${field.label}</label>
            <input
              type="${field.type}"
              id="${field.id}"
              placeholder="${field.placeholder || ''}"
              ${field.required ? 'required' : ''}
              ${field.pattern ? `pattern="${field.pattern}"` : ''}
              ${field.value ? `value="${field.value}"` : ''}
            >
            ${field.small ? `<small>${field.small}</small>` : ''}
          </div>
        `;
      })
      .join('');

    const turnstileWidget = this.turnstileContainerId
      ? `<div class="turnstile-container"><div id="${this.turnstileContainerId}"></div></div>`
      : '';

    const secondaryButtons = (this.config.secondaryButtons || [])
      .map(
        (btn) =>
          `<button type="button" class="btn btn-secondary" data-action="${btn.action}">${btn.text}</button>`
      )
      .join('');

    return `
      <div class="form-section">
        <h2>
          ${this.config.title}
          ${badges}
        </h2>
        <p style="color: #666; margin-bottom: 15px;">${this.config.description}</p>

        <form id="form-${this.config.id}">
          ${fields}
          ${turnstileWidget}
          <button type="submit" class="btn btn-primary">${this.config.submitButton}</button>
          ${secondaryButtons}
        </form>

        <div class="response-section" id="${this.responseElementId}" style="display: none;"></div>
      </div>
    `;
  }

  private setupFormListener(): void {
    const form = document.getElementById(`form-${this.config.id}`) as HTMLFormElement;
    if (form) {
      form.addEventListener('submit', (e) => this.handleSubmit(e));
    }
  }

  private setupSecondaryButtons(): void {
    const buttons = this.container.querySelectorAll('button[data-action]');
    buttons.forEach((button) => {
      const action = button.getAttribute('data-action');
      button.addEventListener('click', () => this.handleSecondaryAction(action!));
    });
  }

  private async handleSubmit(event: Event): Promise<void> {
    event.preventDefault();

    const formData = this.getFormData();
    const turnstileToken = this.turnstileManager?.getToken();

    try {
      let result: ApiResponse;

      // Route to appropriate API method based on form ID
      switch (this.config.id) {
        case 'signup':
          result = await this.authAPI.signup(formData.email, formData.password, turnstileToken || undefined);
          break;
        case 'login':
          result = await this.authAPI.login(formData.email, formData.password);
          if (result.status === 200 && result.data.idToken) {
            this.authStore.setTokens(result.data);
          }
          break;
        case 'verify-email':
          result = await this.authAPI.verifyEmail(formData.email, formData.code);
          break;
        case 'forgot-password':
          result = await this.authAPI.forgotPassword(formData.email, turnstileToken || undefined);
          break;
        case 'reset-password':
          result = await this.authAPI.resetPassword(
            formData.email,
            formData.code,
            formData.newPassword,
            turnstileToken || undefined
          );
          break;
        case 'resend-code':
          result = await this.authAPI.resendCode(formData.email);
          break;
        case 'refresh':
          const refreshToken = formData.refreshToken || this.authStore.getRefreshToken();
          if (!refreshToken) {
            throw new Error('No refresh token available');
          }
          result = await this.authAPI.refreshToken(refreshToken);
          if (result.status === 200 && result.data.idToken) {
            this.authStore.setTokens({ idToken: result.data.idToken, accessToken: result.data.accessToken });
          }
          break;
        case 'validate':
          const validateToken = formData.token || this.authStore.getIdToken();
          if (!validateToken) {
            throw new Error('No token available');
          }
          result = await this.authAPI.validateToken(validateToken);
          break;
        case 'signout':
          result = await this.authAPI.signout(formData.globalSignOut === 'true');
          if (result.status === 200) {
            this.authStore.clearTokens();
          }
          break;
        case 'invalidate-sessions':
          result = await this.authAPI.invalidateSessions();
          if (result.status === 200) {
            this.authStore.clearTokens();
          }
          break;
        case 'upgrade':
          result = await this.authAPI.createCheckoutSession(
            formData.successUrl,
            formData.cancelUrl,
            formData.billingCycle
          );
          if (result.status === 200 && result.data.session?.url) {
            localStorage.setItem('pending-upgrade', 'true');
            this.showCheckoutRedirect(result.data);
            return;
          }
          break;
        case 'cancel-subscription':
          result = await this.authAPI.cancelSubscription(
            formData.subscriptionId,
            formData.atPeriodEnd === 'true'
          );
          break;
        default:
          throw new Error(`Unknown form type: ${this.config.id}`);
      }

      this.showResponse(this.responseElementId, result.status, result.data, result.headers);

      // Reset Turnstile after submission
      if (this.turnstileContainerId && this.turnstileManager) {
        this.turnstileManager.resetWidget(this.turnstileContainerId);
      }
    } catch (error) {
      this.showResponse(this.responseElementId, 0, {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }

  private getFormData(): Record<string, string> {
    const data: Record<string, string> = {};

    this.config.fields.forEach((field) => {
      const element = document.getElementById(field.id) as
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement;

      if (element) {
        if (field.type === 'checkbox') {
          data[field.id] = (element as HTMLInputElement).checked.toString();
        } else {
          data[field.id] = element.value;
        }
      }
    });

    return data;
  }

  private handleSecondaryAction(action: string): void {
    switch (action) {
      case 'use-stored-refresh-token':
        const refreshToken = this.authStore.getRefreshToken();
        if (refreshToken) {
          const textarea = document.getElementById('refreshToken') as HTMLTextAreaElement;
          if (textarea) textarea.value = refreshToken;
        }
        break;
      case 'use-stored-id-token':
        const idToken = this.authStore.getIdToken();
        if (idToken) {
          const textarea = document.getElementById('token') as HTMLTextAreaElement;
          if (textarea) textarea.value = idToken;
        }
        break;
      case 'reset-form':
        const form = document.getElementById(`form-${this.config.id}`) as HTMLFormElement;
        if (form) form.reset();
        break;
    }
  }

  private showCheckoutRedirect(data: any): void {
    const checkoutUrl = data.session?.url || data.url;
    const responseEl = document.getElementById(this.responseElementId);
    if (!responseEl) return;

    responseEl.style.display = 'block';
    responseEl.innerHTML = `
      <h4>Response</h4>
      <span class="status success">HTTP 200</span>
      <pre>${JSON.stringify(data, null, 2)}</pre>
      <div style="margin-top: 15px; padding: 15px; background: #dbeafe; border-radius: 6px; border: 1px solid #60a5fa;">
        <h4 style="margin: 0 0 10px 0; color: #1e40af;">Ready to checkout</h4>
        <p style="margin: 0 0 10px 0; font-size: 14px; color: #1e40af;">
          Click the button below to proceed to Stripe's secure payment page:
        </p>
        <a href="${checkoutUrl}" target="_blank" class="btn btn-primary" style="display: inline-block; text-decoration: none;">
          Go to Stripe Checkout
        </a>
      </div>
    `;
  }

  destroy(): void {
    // Remove Turnstile widget
    if (this.turnstileContainerId && this.turnstileManager) {
      this.turnstileManager.removeWidget(this.turnstileContainerId);
    }
    super.destroy();
  }
}

// Export form configurations
export const AUTH_FORM_CONFIGS: Record<TabId, AuthFormConfig> = {
  signup: {
    id: 'signup',
    title: 'Sign Up',
    description: '',
    badges: [
      { text: 'Turnstile Required', class: 'turnstile' },
      { text: 'Bot Check', class: 'bot-check' },
      { text: '1/min', class: 'rate' },
    ],
    requiresTurnstile: true,
    fields: [
      { id: 'email', label: 'Email', type: 'email', placeholder: 'user@example.com', required: true },
      {
        id: 'password',
        label: 'Password',
        type: 'password',
        placeholder: 'Min 8 chars, uppercase, lowercase, number, special',
        required: true,
        small: 'Must meet Cognito policy: 8+ chars, uppercase, lowercase, number, special char',
      },
    ],
    submitButton: 'Sign Up',
  },
  login: {
    id: 'login',
    title: 'Login',
    description: '',
    badges: [
      { text: 'Bot Check', class: 'bot-check' },
      { text: '10/min', class: 'rate' },
    ],
    requiresTurnstile: false,
    fields: [
      { id: 'email', label: 'Email', type: 'email', placeholder: 'user@example.com', required: true },
      { id: 'password', label: 'Password', type: 'password', placeholder: 'Your password', required: true },
    ],
    submitButton: 'Login',
  },
  'verify-email': {
    id: 'verify-email',
    title: 'Verify Email',
    description: '',
    badges: [{ text: '10/min', class: 'rate' }],
    requiresTurnstile: false,
    fields: [
      { id: 'email', label: 'Email', type: 'email', placeholder: 'user@example.com', required: true },
      {
        id: 'code',
        label: 'Verification Code',
        type: 'text',
        placeholder: '123456',
        required: true,
        pattern: '[0-9]{6}',
        small: '6-digit code sent to your email',
      },
    ],
    submitButton: 'Verify Email',
  },
  'forgot-password': {
    id: 'forgot-password',
    title: 'Forgot Password',
    description: '',
    badges: [
      { text: 'Turnstile Required', class: 'turnstile' },
      { text: 'Bot Check', class: 'bot-check' },
      { text: '1/min', class: 'rate' },
    ],
    requiresTurnstile: true,
    fields: [{ id: 'email', label: 'Email', type: 'email', placeholder: 'user@example.com', required: true }],
    submitButton: 'Send Reset Code',
  },
  'reset-password': {
    id: 'reset-password',
    title: 'Reset Password',
    description: '',
    badges: [
      { text: 'Turnstile Required', class: 'turnstile' },
      { text: 'Bot Check', class: 'bot-check' },
      { text: '1/min', class: 'rate' },
    ],
    requiresTurnstile: true,
    fields: [
      { id: 'email', label: 'Email', type: 'email', placeholder: 'user@example.com', required: true },
      { id: 'code', label: 'Reset Code', type: 'text', placeholder: '123456', required: true, pattern: '[0-9]{6}' },
      { id: 'newPassword', label: 'New Password', type: 'password', placeholder: 'New secure password', required: true },
    ],
    submitButton: 'Reset Password',
  },
  'resend-code': {
    id: 'resend-code',
    title: 'Resend Verification Code',
    description: '',
    badges: [
      { text: 'Bot Check', class: 'bot-check' },
      { text: '1/min', class: 'rate' },
    ],
    requiresTurnstile: false,
    fields: [{ id: 'email', label: 'Email', type: 'email', placeholder: 'user@example.com', required: true }],
    submitButton: 'Resend Code',
  },
  refresh: {
    id: 'refresh',
    title: 'Refresh Token',
    description: '',
    badges: [{ text: '30/min', class: 'rate' }],
    requiresTurnstile: false,
    fields: [
      {
        id: 'refreshToken',
        label: 'Refresh Token',
        type: 'textarea',
        placeholder: 'Paste refresh token or leave empty to use stored token',
        required: false,
        small: 'Leave empty to use token from localStorage',
      },
    ],
    submitButton: 'Refresh',
    secondaryButtons: [{ text: 'Use Stored Token', action: 'use-stored-refresh-token' }],
  },
  validate: {
    id: 'validate',
    title: 'Validate Token',
    description:
      'Validate a JWT token and retrieve the signed context. This also seeds/initializes user records in DynamoDB for hosted-UI logins.',
    badges: [
      { text: 'Auth Required', class: 'auth' },
      { text: '30/min', class: 'rate' },
    ],
    requiresTurnstile: false,
    fields: [
      {
        id: 'token',
        label: 'Token to Validate',
        type: 'textarea',
        placeholder: 'Paste JWT token or leave empty to use stored ID token',
        required: false,
        small: 'Leave empty to use ID token from localStorage',
      },
    ],
    submitButton: 'Validate Token',
    secondaryButtons: [{ text: 'Use Stored ID Token', action: 'use-stored-id-token' }],
  },
  signout: {
    id: 'signout',
    title: 'Sign Out',
    description: '',
    badges: [
      { text: 'Auth Required', class: 'auth' },
      { text: '10/min', class: 'rate' },
    ],
    requiresTurnstile: false,
    fields: [
      {
        id: 'globalSignOut',
        label: 'Global sign out (all devices via Cognito)',
        type: 'checkbox',
        required: false,
      },
    ],
    submitButton: 'Sign Out',
  },
  'invalidate-sessions': {
    id: 'invalidate-sessions',
    title: 'Invalidate All Sessions',
    description:
      'This will invalidate ALL sessions for your account across all devices. You will need to log in again everywhere.',
    badges: [
      { text: 'Auth Required', class: 'auth' },
      { text: '1/min', class: 'rate' },
    ],
    requiresTurnstile: false,
    fields: [],
    submitButton: 'Invalidate All Sessions',
  },
  upgrade: {
    id: 'upgrade',
    title: 'Create Upgrade Checkout Session',
    description:
      "Create a Stripe checkout session to upgrade your account to pro tier. You'll be redirected to Stripe's secure payment page.",
    badges: [
      { text: 'Auth Required', class: 'auth' },
      { text: '10/hour, 50/day', class: 'rate' },
    ],
    requiresTurnstile: false,
    fields: [
      {
        id: 'successUrl',
        label: 'Success URL',
        type: 'url',
        placeholder: 'https://admin.consenger.app/?upgrade=success',
        value: 'https://admin.consenger.app/?upgrade=success',
        required: true,
        small: 'Redirect URL after successful payment (must be https://consenger.app/* or https://admin.consenger.app/*)',
      },
      {
        id: 'cancelUrl',
        label: 'Cancel URL',
        type: 'url',
        placeholder: 'https://consenger.app/checkout/cancel',
        value: 'https://admin.consenger.app/checkout/cancel',
        required: true,
        small: 'Redirect URL if payment is cancelled',
      },
      {
        id: 'billingCycle',
        label: 'Billing Cycle',
        type: 'select',
        required: true,
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'yearly', label: 'Yearly' },
        ],
      },
    ],
    submitButton: 'Create Checkout Session',
  },
  'cancel-subscription': {
    id: 'cancel-subscription',
    title: 'Cancel Subscription',
    description:
      'Cancel your active subscription. You can choose to cancel immediately or at the end of the billing period.',
    badges: [
      { text: 'Auth Required', class: 'auth' },
      { text: '5/hour, 20/day', class: 'rate' },
    ],
    requiresTurnstile: false,
    fields: [
      {
        id: 'subscriptionId',
        label: 'Subscription ID',
        type: 'text',
        placeholder: 'sub_1234567890',
        pattern: 'sub_.*',
        required: true,
        small: 'Enter your Stripe subscription ID (starts with "sub_")',
      },
      {
        id: 'atPeriodEnd',
        label: 'Cancel at end of billing period (recommended)',
        type: 'checkbox',
        required: false,
        small: 'If unchecked, subscription will be canceled immediately with no refund',
      },
    ],
    submitButton: 'Cancel Subscription',
    secondaryButtons: [{ text: 'Reset Form', action: 'reset-form' }],
  },
  'streaming-chat': {} as any,
  'single-prompt': {} as any,
  tts: {} as any,
  'tts-streaming': {} as any,
  'model-config': {} as any,
  'task-routing': {} as any,
  'ai-routing': {} as any,
  'tier-enforcement': {} as any,
  'service-clients': {} as any,
  'routing-visualizer': {} as any,
  'livekit-test': {} as any,
  'game-generation': {} as any,
};
